<template>
  <div class="container py-5 overflow-y-auto">
    <div class="row justify-content-between align-items-center mb-4">
      <div class="col">
        <h1>ESO → Grexel Status</h1>
      </div>
      <div class="col-auto d-flex gap-2">
        <button class="btn btn-primary" @click="handleButtonClick">
          <i class="fas fa-sync me-2"></i>Refresh Data
        </button>
      </div>
    </div>

    <!-- Status Summary Cards -->
    <div class="row g-3 mb-4">
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Total Companies</h5>
            <h2 class="mb-0">{{ filteredAndSortedData.length }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Pending Approval</h5>
            <h2 class="mb-0">{{ statusCounts.pending }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Issued/Approved</h5>
            <h2 class="mb-0">{{ statusCounts.approved }}</h2>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Rejected</h5>
            <h2 class="mb-0">{{ statusCounts.rejected }}</h2>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Legend -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="card-title mb-3">Status Legend</h5>
            <div class="row g-3">
              <div class="col-md-6">
                <div class="d-flex flex-column gap-2">
                  <div class="d-flex align-items-center">
                    <span class="status-badge bg-warning"></span>
                    <span class="ms-2">Pending Approval</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="status-badge bg-success"></span>
                    <span class="ms-2">Issued</span>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="d-flex flex-column gap-2">
                  <div class="d-flex align-items-center">
                    <span class="status-badge bg-info"></span>
                    <span class="ms-2">Approved</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="status-badge bg-danger"></span>
                    <span class="ms-2">Rejected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="alert alert-warning mb-0" style="max-width: 400px">
            <i class="fas fa-exclamation-triangle me-2"></i>
            <strong>Note:</strong> Grexel auth session must be active for this
            page to work
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h5 class="mb-0">Monthly Generation Data</h5>
        <div class="d-flex gap-2 align-items-center">
          <div class="input-group" style="width: 300px">
            <input
              type="text"
              class="form-control"
              v-model="companySearch"
              placeholder="Search companies..."
            />
          </div>
          <div class="btn-group">
            <button
              class="btn btn-outline-secondary"
              :class="{ active: statusFilter === 'all' }"
              @click="statusFilter = 'all'"
            >
              All
            </button>
            <button
              class="btn btn-outline-warning"
              :class="{ active: statusFilter === 'pending' }"
              @click="statusFilter = 'pending'"
            >
              Pending
            </button>
            <button
              class="btn btn-outline-success"
              :class="{ active: statusFilter === 'approved' }"
              @click="statusFilter = 'approved'"
            >
              Approved
            </button>
            <button
              class="btn btn-outline-danger"
              :class="{ active: statusFilter === 'rejected' }"
              @click="statusFilter = 'rejected'"
            >
              Rejected
            </button>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th @click="sort('company_name')" class="sortable">
                Company Name
                <i class="fas" :class="getSortIcon('company_name')"></i>
              </th>
              <th @click="sort('company_id')" class="sortable">
                Company ID
                <i class="fas" :class="getSortIcon('company_id')"></i>
              </th>
              <th @click="sort('grid_reference')" class="sortable">
                Grid Reference
                <i class="fas" :class="getSortIcon('grid_reference')"></i>
              </th>
              <th @click="sort('gsrn')" class="sortable">
                GSRN
                <i class="fas" :class="getSortIcon('gsrn')"></i>
              </th>
              <th
                v-for="month in months"
                :key="month"
                @click="sort(month)"
                class="sortable"
              >
                {{ month }}
                <i class="fas" :class="getSortIcon(month)"></i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="company in filteredAndSortedData"
              :key="company.company_id!"
            >
              <td>{{ company.company_name }}</td>
              <td>{{ company.company_id }}</td>
              <td :title="`Grid Reference: ${company.grid_reference}`">
                <span class="grid-reference-cell">{{
                  company.grid_reference
                }}</span>
              </td>
              <td :title="`GSRN: ${company.gsrn}`">
                <span class="gsrn-cell">{{ company.gsrn }}</span>
              </td>
              <td v-for="month in months" :key="month">
                <div
                  class="status-cell"
                  :class="{
                    'bg-warning':
                      company.meter_readings[month]?.status ===
                      'PENDING_APPROVAL',
                    'bg-success':
                      company.meter_readings[month]?.status === 'ISSUED',
                    'bg-info':
                      company.meter_readings[month]?.status === 'APPROVED',
                    'bg-danger':
                      company.meter_readings[month]?.status === 'REJECTED',
                  }"
                  :title="getStatusTooltip(company, month)"
                >
                  {{ company.meter_readings[month]?.value ?? "N/A" }}
                </div>
              </td>
            </tr>
            <tr v-if="filteredAndSortedData.length === 0">
              <td :colspan="months.length + 4" class="text-center py-4">
                No data available
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { goSolidApi } from "@/composables/useGoSolidApi";
import { computed, onMounted, ref } from "vue";

interface MeterReading {
  date: string;
  status: string;
  value: number;
}

interface CompanyData {
  company_id: number | null;
  company_name: string | null;
  meter_readings: Record<string, MeterReading>;
  grid_reference: string | null;
  gsrn: string | null;
}

type ReadingBucket = "pending" | "approved" | "rejected";

const months = ref<string[]>([]);
const tableData = ref<CompanyData[]>([]);
const companySearch = ref("");
const sortBy = ref<string>("");
const sortDesc = ref(false);
const statusFilter = ref<"all" | ReadingBucket>("all");

const filteredTableData = computed(() => {
  let filtered = tableData.value;

  // Apply search filter
  if (companySearch.value) {
    const search = companySearch.value.toLowerCase();
    filtered = filtered.filter(
      (company) =>
        (company.company_name?.toLowerCase() || "").includes(search) ||
        String(company.company_id).includes(search) ||
        (company.grid_reference?.toLowerCase() || "").includes(search) ||
        (company.gsrn?.toLowerCase() || "").includes(search)
    );
  }

  // Apply status filter
  if (statusFilter.value !== "all") {
    filtered = filtered.filter((company) => {
      return Object.values(company.meter_readings).some(
        (reading) => readingBucket(reading.status) === statusFilter.value
      );
    });
  }

  return filtered;
});

const statusCounts = computed(() => {
  const counts = {
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  tableData.value.forEach((company) => {
    Object.values(company.meter_readings).forEach((reading) => {
      const bucket = readingBucket(reading.status);
      if (bucket) counts[bucket]++;
    });
  });

  return counts;
});

/** The summary bucket a meter reading's status counts towards; null for statuses outside the three cards. */
const readingBucket = (status: string): ReadingBucket | null => {
  switch (status) {
    case "PENDING_APPROVAL":
      return "pending";
    case "ISSUED":
    case "APPROVED":
      return "approved";
    case "REJECTED":
      return "rejected";
    default:
      return null;
  }
};

const getStatusTooltip = (company: CompanyData, month: string): string => {
  const reading = company.meter_readings[month];
  if (!reading) return "No data available";
  return `Status: ${reading.status}\nValue: ${reading.value}\nDate: ${reading.date}`;
};

const sort = (field: string) => {
  if (sortBy.value === field) {
    sortDesc.value = !sortDesc.value;
  } else {
    sortBy.value = field;
    sortDesc.value = false;
  }
};

const getSortIcon = (field: string) => {
  if (sortBy.value !== field) return "fa-sort";
  return sortDesc.value ? "fa-sort-down" : "fa-sort-up";
};

const filteredAndSortedData = computed(() => {
  if (!sortBy.value) return filteredTableData.value;

  return [...filteredTableData.value].sort((a, b) => {
    // Handle company name and ID sorting
    if (
      sortBy.value === "company_name" ||
      sortBy.value === "company_id" ||
      sortBy.value === "grid_reference" ||
      sortBy.value === "gsrn"
    ) {
      let aVal = a[sortBy.value as keyof CompanyData];
      let bVal = b[sortBy.value as keyof CompanyData];

      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return sortDesc.value ? 1 : -1;
      if (aStr > bStr) return sortDesc.value ? -1 : 1;
      return 0;
    }

    // Handle status column sorting
    const aStatus = a.meter_readings[sortBy.value]?.status || "";
    const bStatus = b.meter_readings[sortBy.value]?.status || "";

    // Define status priority (higher number = higher priority)
    const statusPriority: Record<string, number> = {
      REJECTED: 4,
      PENDING_APPROVAL: 3,
      APPROVED: 2,
      ISSUED: 1,
      "": 0,
    };

    const aPriority = statusPriority[aStatus];
    const bPriority = statusPriority[bStatus];

    if (aPriority !== bPriority) {
      return sortDesc.value ? bPriority - aPriority : aPriority - bPriority;
    }

    // If status is the same, sort by value
    const aValue = a.meter_readings[sortBy.value]?.value || 0;
    const bValue = b.meter_readings[sortBy.value]?.value || 0;

    return sortDesc.value ? bValue - aValue : aValue - bValue;
  });
});

const fetchData = async () => {
  try {
    const response = await goSolidApi.get(
      "/api/admin/integration/grexel/meter-reading-status/"
    );
    if (response.status === 200 && response.data) {
      // Get only the last 3 months
      months.value = response.data.months.slice(-3);
      const tmp: CompanyData[] = Object.values(response.data.data);
      tmp.sort((a: CompanyData, b: CompanyData) =>
        (a.company_name ?? "").localeCompare(b.company_name ?? "")
      );
      tableData.value = tmp;
    }
  } catch (error: any) {
    console.error("Error fetching data:", error);
  }
};

onMounted(() => {
  fetchData();
});

const handleButtonClick = () => {
  fetchData();
};
</script>

<style scoped lang="scss">
.card {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
}

.card-header {
  background-color: transparent;
  border-bottom: 1px solid var(--phoenix-gray-200);
  padding: 1rem;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  white-space: nowrap;
}

.status-badge {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  display: inline-block;
}

.status-cell {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  min-width: 80px;
}

/* Status colors */
.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.bg-success {
  background-color: #198754 !important;
  color: #fff !important;
}

.bg-info {
  background-color: #0dcaf0 !important;
  color: #000 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
  color: #fff !important;
}

.alert {
  margin-bottom: 0;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffecb5;
  color: #664d03;
}

/* Status legend colors */
.status-badge.bg-warning {
  background-color: #ffc107 !important;
}

.status-badge.bg-success {
  background-color: #198754 !important;
}

.status-badge.bg-info {
  background-color: #0dcaf0 !important;
}

.status-badge.bg-danger {
  background-color: #dc3545 !important;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: var(--phoenix-gray-100);
}

.sortable i {
  margin-left: 0.5rem;
  color: var(--phoenix-gray-400);
}

.sortable i.fa-sort-up,
.sortable i.fa-sort-down {
  color: var(--phoenix-primary);
}

.btn-group .btn.active {
  z-index: 1;
}

.btn-outline-warning.active {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #000;
}

.btn-outline-success.active {
  background-color: #198754;
  border-color: #198754;
  color: #fff;
}

.btn-outline-danger.active {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #fff;
}

.grid-reference-cell {
  font-family: monospace;
  background-color: var(--phoenix-gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.gsrn-cell {
  font-family: monospace;
  background-color: var(--phoenix-gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}
</style>
